import React from 'react';

import { studentMessages } from '../../../../../../translates/studentMessages';
import { trackingMessages } from '../../../../../../translates/trackingMessages';
import { useIntlReexport } from '../../../../../hooks/useIntlReexport';
import { useStudentSelector } from '../../../../../store/selectors/useStudentSelector';
import { SubmenuItem } from '../../../containers/student/SubmenuItem';
import { getSubmenuGroupSelector } from '../../../selectors/getSubmenuGroup';
import { NavigationMenuItem } from './NavigationMenuItem';
import { Item, MenuItem, TrackingStatus } from '@ael/core-api';
import {
  CompletionStatusLabel,
  MenuItemLabel,
  createCompletedOfTotalProgressDisplayConfiguration,
} from '@ael/shared-components-library';
import AnimateHeight from 'react-animate-height';

interface SubmenuNavigationMenuItemProps {
  item: MenuItem;
  parentItemId: string | null;
  nestedLevel: number;
  isLinearModeOn: boolean;
  TrackingStatusProvider: React.FC<{ itemId: string; render: (trackingStatus: TrackingStatus) => React.ReactElement }>;
  isStudent: boolean;
  getItemById: (itemId: string) => Item;
}

export const SubmenuNavigationMenuItem: React.FC<SubmenuNavigationMenuItemProps> = React.memo(
  ({ item, parentItemId, nestedLevel, getItemById, isLinearModeOn, TrackingStatusProvider, isStudent }) => {
    const getSubmenuGroup = useStudentSelector(getSubmenuGroupSelector);
    const { formatMessage } = useIntlReexport();
    const submenuGroup = getSubmenuGroup(item.id);
    const childItems = submenuGroup ? submenuGroup.childItems : undefined;
    const isExpanded = submenuGroup ? submenuGroup.isExpanded : false;
    const isActive = submenuGroup ? submenuGroup.isActive : false;

    return (
      // div wrapper is required for the AnimateHeight to work correctly
      <div>
        <TrackingStatusProvider
          itemId={item.id}
          render={(trackingStatus: TrackingStatus) => (
            <SubmenuItem
              item={item}
              key={item.id}
              trackingStatus={trackingStatus}
              expanded={isActive}
              hasActiveChild={isExpanded}
              parentItemId={parentItemId}
              nestedLevel={nestedLevel}
              isLinearModeOn={isLinearModeOn}
            >
              <MenuItemLabel>{item.title}</MenuItemLabel>
              {submenuGroup && (
                <CompletionStatusLabel
                  progressDetails={
                    submenuGroup.completableChildItems > 0
                      ? {
                          hasProgress: true,
                          progress: createCompletedOfTotalProgressDisplayConfiguration(
                            submenuGroup.completableChildItems,
                            submenuGroup.completedChildItems
                          ),
                          isActive,
                          isSemiActive: isExpanded,
                          hideChevron: true,
                          showItemCompletion: submenuGroup.completableChildItems > 0,
                        }
                      : { hasProgress: false }
                  }
                  messages={{
                    emptyItemText: formatMessage(studentMessages.emptyItem),
                    completionLabelText: formatMessage(trackingMessages.completed),
                    optionalItemText: formatMessage(trackingMessages.optional),
                    passedText: formatMessage(trackingMessages.passed),
                    incompleteText: formatMessage(trackingMessages.notPassed),
                    notStartedText: formatMessage(trackingMessages.notStarted),
                  }}
                />
              )}
            </SubmenuItem>
          )}
        />
        {childItems && (
          <AnimateHeight height={isExpanded ? 'auto' : 0} duration={500} contentClassName="navigation-menu__content">
            <div className="navigation-menu__submenu-wrapper">
              {childItems.map((itemId: string) => (
                <NavigationMenuItem
                  key={itemId}
                  itemId={itemId}
                  parentItemId={item.id}
                  nestedLevel={nestedLevel + 1}
                  getItemById={getItemById}
                  isLinearModeOn={isLinearModeOn}
                  isStudent={isStudent}
                />
              ))}
            </div>
          </AnimateHeight>
        )}
      </div>
    );
  }
);
